/**
 * Unified Hook for CD Form Management
 *
 * Combines Zustand store state with TanStack Query data loading.
 * Provides a single, consistent interface for CD components across
 * create, edit, and preview modes.
 *
 * @example
 * ```tsx
 * function CDEditPage({ transactionId }: { transactionId: string }) {
 *   const {
 *     formData,
 *     isLoading,
 *     isDirty,
 *     editable,
 *     updateField,
 *     resetForm,
 *   } = useCDForm(transactionId, 'edit');
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <CDPage1
 *       data={formData.page1}
 *       editable={editable}
 *       onChange={(data) => updateField('page1', data)}
 *     />
 *   );
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import {
  selectCanRedo,
  selectCanUndo,
  selectIsDirty,
  selectLoadError,
  selectLoadingState,
  selectSyncStatus,
  selectValidationErrors,
  useClosingDisclosureStore,
} from '../store/closingDisclosureStore';
import type { ClosingDisclosureData } from '../types';
import { type CDMode, useCDData } from './useCDData';

/**
 * Return type for useCDForm hook
 */
export interface UseCDFormResult {
  // ============================================================================
  // Form Data
  // ============================================================================

  /**
   * Current form data (5-page structure)
   */
  formData: ClosingDisclosureData;

  /**
   * Whether form has unsaved changes
   */
  isDirty: boolean;

  /**
   * Sync status with server
   */
  syncStatus: 'idle' | 'saving' | 'saved' | 'error';

  /**
   * Validation errors by field path
   */
  validationErrors: Record<string, string>;

  // ============================================================================
  // Loading States
  // ============================================================================

  /**
   * Store loading state ('idle' | 'loading' | 'loaded' | 'error')
   */
  loadingState: 'idle' | 'loading' | 'loaded' | 'error';

  /**
   * Store load error message
   */
  loadError: string | null;

  /**
   * Whether data is being fetched from server
   */
  isLoading: boolean;

  /**
   * Whether all data has been successfully loaded
   */
  isReady: boolean;

  /**
   * Whether any error occurred during loading
   */
  isError: boolean;

  /**
   * Error object if loading failed
   */
  error: Error | null;

  // ============================================================================
  // Mode Information
  // ============================================================================

  /**
   * Current operation mode
   */
  mode: CDMode;

  /**
   * Whether form fields are editable (false in preview mode)
   */
  editable: boolean;

  // ============================================================================
  // Undo/Redo
  // ============================================================================

  /**
   * Whether undo is available
   */
  canUndo: boolean;

  /**
   * Whether redo is available
   */
  canRedo: boolean;

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Update a specific field by path
   * @example updateField('page1.header.closingDate', '2024-01-15')
   */
  updateField: (path: string, value: unknown) => void;

  /**
   * Update entire page data
   * @example updatePage('page1', { header: { ... } })
   */
  updatePage: (
    page: 'page1' | 'page2' | 'page3' | 'page4' | 'page5',
    data: Partial<ClosingDisclosureData[typeof page]>,
  ) => void;

  /**
   * Reset form to last saved server state
   */
  resetToServer: () => void;

  /**
   * Reset form to default empty state
   */
  resetForm: () => void;

  /**
   * Undo last change
   */
  undo: () => void;

  /**
   * Redo previously undone change
   */
  redo: () => void;

  /**
   * Set a validation error for a field
   */
  setValidationError: (path: string, error: string) => void;

  /**
   * Clear validation error for a field
   */
  clearValidationError: (path: string) => void;

  /**
   * Clear all validation errors
   */
  clearValidationErrors: () => void;

  /**
   * Refetch all data from server
   */
  refetchAll: () => Promise<void>;

  /**
   * Manually initialize the store with current query data
   */
  initializeStore: () => void;

  /**
   * Get list of changed field paths
   */
  getChangedFields: () => string[];

  /**
   * Check if form has any changes from original
   */
  hasChanges: () => boolean;
}

/**
 * Unified hook for CD form management
 *
 * Combines server data fetching (TanStack Query) with client state management (Zustand)
 * to provide a seamless experience across create, edit, and preview modes.
 *
 * @param transactionId - Transaction ID to load (undefined for create mode)
 * @param mode - Operation mode: 'create', 'edit', or 'preview'
 * @returns Combined form state and actions
 */
export function useCDForm(transactionId: string | undefined, mode: CDMode): UseCDFormResult {
  // ============================================================================
  // Store Access
  // ============================================================================

  const store = useClosingDisclosureStore();

  // Use selectors for derived state
  const isDirty = useClosingDisclosureStore(selectIsDirty);
  const syncStatus = useClosingDisclosureStore(selectSyncStatus) as UseCDFormResult['syncStatus'];
  const validationErrors = useClosingDisclosureStore(selectValidationErrors);
  const loadingState = useClosingDisclosureStore(selectLoadingState);
  const loadError = useClosingDisclosureStore(selectLoadError);
  const canUndo = useClosingDisclosureStore(selectCanUndo);
  const canRedo = useClosingDisclosureStore(selectCanRedo);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const cdData = useCDData({
    transactionId,
    mode,
    enabled: mode !== 'create',
    autoInitialize: true,
  });

  // ============================================================================
  // Memoized Actions
  // ============================================================================

  const updateField = useCallback(
    (path: string, value: unknown) => {
      store.updateField(path, value);
    },
    [store],
  );

  const updatePage = useCallback(
    (
      page: 'page1' | 'page2' | 'page3' | 'page4' | 'page5',
      data: Partial<ClosingDisclosureData[typeof page]>,
    ) => {
      store.updatePage(page, data);
    },
    [store],
  );

  const resetToServer = useCallback(() => {
    store.resetToServer();
  }, [store]);

  const resetForm = useCallback(() => {
    store.resetForm();
  }, [store]);

  const undo = useCallback(() => {
    store.undo();
  }, [store]);

  const redo = useCallback(() => {
    store.redo();
  }, [store]);

  const setValidationError = useCallback(
    (path: string, error: string) => {
      store.setValidationError(path, error);
    },
    [store],
  );

  const clearValidationError = useCallback(
    (path: string) => {
      store.clearValidationError(path);
    },
    [store],
  );

  const clearValidationErrors = useCallback(() => {
    store.clearValidationErrors();
  }, [store]);

  const getChangedFields = useCallback(() => {
    return store.getChangedFields();
  }, [store]);

  const hasChanges = useCallback(() => {
    return store.hasChanges();
  }, [store]);

  // ============================================================================
  // Derived Values
  // ============================================================================

  const editable = useMemo(() => mode !== 'preview', [mode]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Form Data
    formData: store.formData,
    isDirty,
    syncStatus,
    validationErrors,

    // Loading States
    loadingState,
    loadError,
    isLoading: cdData.isLoading,
    isReady: cdData.isReady,
    isError: cdData.isError,
    error: cdData.error,

    // Mode Information
    mode,
    editable,

    // Undo/Redo
    canUndo,
    canRedo,

    // Actions
    updateField,
    updatePage,
    resetToServer,
    resetForm,
    undo,
    redo,
    setValidationError,
    clearValidationError,
    clearValidationErrors,
    refetchAll: cdData.refetchAll,
    initializeStore: cdData.initializeStore,
    getChangedFields,
    hasChanges,
  };
}

export default useCDForm;
